import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import {
  Users, FileText, CalendarDays, Handshake, Globe, Cpu,
  AlertTriangle, User, Bot, ChevronDown,
  Loader2, RefreshCw, Clock, Zap, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const SEV = {
  active:   { bg: 'bg-emerald-500/10', border: 'border-l-emerald-500', text: 'text-emerald-400', label: 'Attivo' },
  slowing:  { bg: 'bg-amber-500/10',   border: 'border-l-amber-500',   text: 'text-amber-400',   label: 'Rallentato' },
  stalled:  { bg: 'bg-orange-500/10',  border: 'border-l-orange-500',  text: 'text-orange-400',  label: 'Fermo' },
  critical: { bg: 'bg-red-500/10',     border: 'border-l-red-500',     text: 'text-red-400',     label: 'Critico' },
  inactive: { bg: 'bg-slate-500/10',   border: 'border-l-slate-600',   text: 'text-slate-500',   label: 'Inattivo' },
};

const ICONS = { crm: Users, content: FileText, events: CalendarDays, sales: Handshake, network: Globe, system: Cpu };
const OWNER = {
  human:  { icon: User, label: 'Intervento umano', color: 'text-purple-400' },
  agent:  { icon: Bot,  label: 'Agente AI',        color: 'text-emerald-400' },
  system: { icon: Cpu,  label: 'Automatico',       color: 'text-blue-400' },
};

function FlowBar({ pipeline, onSelect, selectedStage }) {
  const total = pipeline.reduce((s, p) => s + Math.max(p.count, 1), 0);
  return (
    <div data-testid="flow-bar" className="flex h-8 rounded-md overflow-hidden gap-px">
      {pipeline.map((p, i) => {
        const width = Math.max((p.count / Math.max(total, 1)) * 100, 8);
        const sev = p.severity_override || (p.count === 0 ? 'inactive' :
          (p.oldest_hours > 168 ? 'critical' : p.oldest_hours > 48 ? 'stalled' :
          p.oldest_hours > 24 ? 'slowing' : 'active'));
        const c = SEV[sev] || SEV.active;
        const O = OWNER[p.owner] || OWNER.system;
        const sel = selectedStage === p.stage;
        return (
          <div key={i} onClick={() => onSelect(sel ? null : p.stage)}
            className={`relative flex items-center justify-center cursor-pointer transition-all duration-300
              ${c.bg} hover:brightness-125 ${sel ? 'ring-2 ring-white/30 scale-[1.02] z-10' : ''}`}
            style={{ width: `${width}%`, minWidth: '36px' }}
            title={`${p.label}: ${p.count} · ${p.note}`}>
            <O.icon className={`w-3 h-3 ${O.color} mr-0.5`} />
            <span className="text-[10px] text-slate-300 font-medium">{p.count}</span>
          </div>
        );
      })}
    </div>
  );
}

function BlockAlert({ block }) {
  const s = SEV[block.severity] || SEV.active;
  return (
    <div data-testid="block-alert" className={`flex items-start gap-2 p-2.5 rounded-lg ${s.bg} border ${s.border.replace('border-l-', 'border-')}/30`}>
      {block.type === 'human'
        ? <User className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
        : <Bot className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium ${s.text}`}>{block.message}</p>
        <p className="text-[10px] text-slate-400 mt-0.5">{block.action}</p>
      </div>
      {block.link && (
        <button onClick={() => window.location.href = block.link}
          className="text-[10px] text-blue-400 hover:text-blue-300 flex-shrink-0 whitespace-nowrap">
          Vai &rarr;
        </button>
      )}
    </div>
  );
}

function AreaCard({ area, expanded, onToggle }) {
  const [selectedStage, setSelectedStage] = useState(null);
  const s = SEV[area.severity] || SEV.active;
  const Icon = ICONS[area.id] || Cpu;
  const hasBlocks = area.blocks?.length > 0;
  const selected = selectedStage ? area.pipeline.find(p => p.stage === selectedStage) : null;

  return (
    <div data-testid={`pulse-area-${area.id}`}
      className={`rounded-xl border-l-4 ${s.border} bg-slate-800/60 backdrop-blur transition-all duration-500
        ${expanded ? 'col-span-1 md:col-span-2 lg:col-span-3' : ''} ${area.severity === 'critical' ? 'animate-pulse-subtle' : ''}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-white/[0.02]" onClick={onToggle}>
        <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
          <Icon className={`w-4.5 h-4.5 ${s.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white">{area.label}</h3>
            <Badge variant="outline" className={`text-[9px] ${s.text} border-current/30 px-1.5 py-0`}>{s.label}</Badge>
            {hasBlocks && (
              <Badge className="bg-red-500/20 text-red-400 text-[9px] px-1.5 py-0">
                {area.blocks.length} blocco{area.blocks.length > 1 ? 'hi' : ''}
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5 truncate">{area.summary}</p>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </div>

      {/* Mini flow bar */}
      <div className="px-4 pb-2">
        <FlowBar pipeline={area.pipeline} onSelect={expanded ? setSelectedStage : () => {}} selectedStage={selectedStage} />
        <div className="flex mt-1">
          {area.pipeline.map((p, i) => {
            const total = area.pipeline.reduce((s, x) => s + Math.max(x.count, 1), 0);
            const width = Math.max((p.count / Math.max(total, 1)) * 100, 8);
            return (
              <div key={i} style={{ width: `${width}%`, minWidth: '36px' }} className="text-center">
                <span className="text-[8px] text-slate-500 truncate block">{p.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-700/50 mt-1 pt-3">
          {hasBlocks && (
            <div className="space-y-2">
              <h4 className="text-[10px] font-semibold text-red-400 uppercase tracking-wider flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Blocchi da risolvere
              </h4>
              {area.blocks.map((b, i) => <BlockAlert key={i} block={b} />)}
            </div>
          )}
          {selected && (
            <div className="bg-slate-900/50 rounded-lg p-3 space-y-2 border border-slate-700/30">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold text-white">{selected.label}</h4>
                {OWNER[selected.owner] && (
                  <span className={`text-[10px] ${OWNER[selected.owner].color} flex items-center gap-1`}>
                    {(() => { const O = OWNER[selected.owner]; return <O.icon className="w-3 h-3" />; })()}
                    {OWNER[selected.owner].label}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400">{selected.note}</p>
              {selected.agent && <p className="text-[10px] text-slate-500">Responsabile: {selected.agent}</p>}
              <div className="flex gap-2 mt-2">
                <button onClick={() => window.location.href = '/command?panel=operator'}
                  className="text-[10px] px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">
                  Chiedi a Operator &rarr;
                </button>
                <button onClick={() => window.location.href = '/command?panel=strategist'}
                  className="text-[10px] px-3 py-1.5 rounded-lg bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 transition-colors">
                  Chiedi a Strategist &rarr;
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PulseMapPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedArea, setExpandedArea] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/pulse-map');
      setData(res.data);
      setLastRefresh(new Date());
    } catch {
      toast.error('Errore caricamento Pulse Map');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const iv = setInterval(fetchData, 60000);
    return () => clearInterval(iv);
  }, [fetchData]);

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
    </div>
  );
  if (!data) return null;

  const critical = data.global_blocks?.filter(b => b.severity === 'critical') || [];

  return (
    <div data-testid="pulse-map-page" className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-400" /> Pulse Map
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Stato in tempo reale del progetto. Click su un'area per espandere.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-[10px] text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastRefresh.toLocaleTimeString('it-CH', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button onClick={fetchData}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors">
            <RefreshCw className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Critical banner */}
      {critical.length > 0 && (
        <div data-testid="critical-banner" className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-xs font-semibold text-red-400">
              {critical.length} blocco{critical.length > 1 ? 'hi' : ''} critico{critical.length > 1 ? 'i' : ''}
            </span>
          </div>
          <div className="space-y-1.5">
            {critical.slice(0, 3).map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-red-300/80">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                <span className="flex-1">{b.message}</span>
                {b.link && (
                  <button onClick={() => window.location.href = b.link}
                    className="text-red-400 hover:underline flex-shrink-0 text-[10px]">Intervieni &rarr;</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Attivo (&lt;24h)</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Rallentato (24-48h)</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" /> Fermo (2-7gg)</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Critico (&gt;7gg)</span>
        <span className="flex items-center gap-1"><User className="w-3 h-3 text-purple-400" /> Serve umano</span>
        <span className="flex items-center gap-1"><Bot className="w-3 h-3 text-emerald-400" /> Agente AI</span>
      </div>

      {/* Area cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.areas.map(area => (
          <AreaCard key={area.id} area={area}
            expanded={expandedArea === area.id}
            onToggle={() => setExpandedArea(expandedArea === area.id ? null : area.id)} />
        ))}
      </div>
    </div>
  );
}
